import React, { useState, useRef, useEffect, act } from "react";
import { io } from "socket.io-client";
import { Box, CircularProgress, MenuItem, Tooltip } from "@mui/material";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { Header, Heading } from "../Summarize/Style";
import { useOutletContext, useNavigate } from "react-router-dom";
import ChatMessage from "./ChatMessage";
import SuggestionMessages from "./SuggestionMessages";
import SkillsSection from "./SkillsSection";
import {
  AvatarSection,
  AssistantName,
  AssistantTitle,
  ChatBox,
  ChatDiv,
  InputContainer,
  Input,
  FileDiv,
  EditImg,
  UploadDiv,
  Container,
  Content,
  NotificationSection,
  TopNavbarSection,
  AddDocument,
  RightNavbarSection,
  SendMessageIcon,
  RightNavItem,
  BelowNavbarSection,
  InnerContainer,
} from "./style";
import { UploadBtn } from "../Summarize/Style";
import BackIcon from "../../assets/back-icon.svg";
import ArrowUpIcon from "../../assets/arrow-up-white.svg";
import PaperClip from "../../assets/paper-clip.svg";
import FileCard from "../Cards/FileCard";
import PdfIcon from "../../assets/pdf-large.svg";
import DocxIcon from "../../assets/docx-large.svg";
import TxtIcon from "../../assets/txt-large.svg";
import { handleFileUploadWithSignedURL } from "../Environment/Asconfig";
import EditIcon from "../../assets/edit-avatar.svg";
import { AVATARS } from "../Constants/Constants";
import Delete from "../../assets/delete-grey.svg";
import ComputerIcon from "../../assets/computer-icon.svg";
import AvatarDriveFilePicker from "./AvatarDriveFilePicker";

import ClearChat from "../../assets/delete-grey.svg";
import RecordIcon from "../../assets/record.png";
import NotificationWhiteIcon from "../../assets/notification_white_icon.svg";
import AvatarChatHomeIcon from "../../assets/avatar_chat_home_icon.svg";
import AvatarChatCameraIcon from "../../assets/avatar_chat_camera_icon.svg";
import AvatarChatBelowIcon from "../../assets/avatar_chat_below_icon.svg";
import PlusIconWhite from "../../assets/plus_icon_white.svg";
import { TopNavItem } from "./style";
import { Divider } from "antd";
import DatabaseChat from "./DatabaseChat";
import { toast } from "react-toastify";
import MeetingChat from "./MeetingChat";
import SkillChat from "./SkillChat";
import { handleSaveChat } from "../../utils/utils";
import { CHAT_BASE_API_URL, API_BASE_URL } from "../../constant/constant";
import { isLastMessageContainFileUrl } from "./isLastMessageContainFileUrl";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../context/SnackBarContext";

const uploadFileToS3 = async (file) => {
  try {
    const response = await handleFileUploadWithSignedURL(file);
    return response.fileUrl;
  } catch (error) {
    toast.error(error);
    return null;
  }
};

const formatTimestamp = (date) => {
  const tzOffset = -date.getTimezoneOffset(); // in minutes
  const diffMs = tzOffset * 60 * 1000; // convert to ms
  const localDate = new Date(date.getTime() + diffMs);

  const offsetSign = tzOffset >= 0 ? "+" : "-";
  const offsetHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(
    2,
    "0",
  );
  const offsetMinutes = String(Math.abs(tzOffset) % 60).padStart(2, "0");

  return `${localDate
    .toISOString()
    .slice(0, 19)}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

const getFileTypeFromName = (fileName) => {
  const extension = fileName.split(".").pop().toLowerCase();
  if (extension === "pdf") return "pdf";
  if (["doc", "docx"].includes(extension)) return "DOCX";
  if (extension === "txt") return "TXT";
  return "unknown";
};

const formatFileFromUrl = (url) => {
  const fileName = decodeURIComponent(url.split("/").pop());
  return {
    name: fileName,
    type: getFileTypeFromName(fileName),
    url: url,
  };
};

const fetchAvatarData = async () => {
  const userId = localStorage.getItem("id");
  const response = await fetch(
    `${API_BASE_URL}/api/auth/get-avatar?user_id=${userId}`,
  );
  const data = await response.json();
  return data;
};

const saveChat = async (
  userId,
  query,
  content,
  files,
  userTimestamp,
  avatarTimestamp,
  isGreetingMsg,
  is_dynamic,
  workflow_data,
) => {
  await fetch(`${API_BASE_URL}/api/auth/avatar-chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-auth-token": localStorage.getItem("token"),
    },
    body: JSON.stringify({
      user_id: userId,
      query,
      content,
      files,
      userTimestamp,
      avatarTimestamp,
      isGreetingMsg,
      is_dynamic,
      workflow_data,
    }),
  });
};

const checkAssistantOnboarded = async (userId) => {
  const response = await fetch(
    `${API_BASE_URL}/api/auth/check-assistant-onboarded`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": localStorage.getItem("token"),
      },
      body: JSON.stringify({ userId }),
    },
  );
  const data = await response.json();
  return data.isAssistantOnboarded;
};

const AvatarChat = () => {
  const { isSidebarOpen } = useOutletContext();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const bottomRef = useRef(null);
  const [isAvatarLoading, setIsAvatarLoading] = useState(true);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(true);
  const [isDriveFilesLoading, setIsDriveFilesLoading] = useState(false);
  const [avatarData, setAvatarData] = useState(null);
  const [clearingChat, setClearingChat] = useState(false);
  // Top Navbar Popover
  const [topPopoverAnchorEl, setTopPopoverAnchorEl] = useState(null);
  // Chat Input Popover
  const [chatPopoverAnchorEl, setChatPopoverAnchorEl] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [activeSection, setActiveSection] = useState("chat");
  const [isUpdatingOnboarding, setIsUpdatingOnboarding] = useState(false);
  const [isDynamic, setIsDynamic] = useState(false);
  const [workflowData, setWorkflowData] = useState({});
  const socketRef = useRef(null);
  const [dbmessage, setDbMessage] = useState([]);
  const [meetingChat, setMeetingChat] = useState([]);
  const [skillChat, setSkillChat] = useState([]);
  const [isChatting, setIsChatting] = useState(false);
  const [activeIcon, setActiveIcon] = useState("ai_assistant");
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    const userId = localStorage.getItem("id");
    if (!userId) return;

    // Use localhost URL for development
    socketRef.current = io(API_BASE_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });

    socketRef.current.on("connect", () => {
      console.log("Socket connected!", socketRef.current.id); // Verify connection
    });

    socketRef.current.on("connect_error", (error) => {
      console.log("Socket connection error:", error); // Debug connection issues
    });

    socketRef.current.emit("join", userId);

    socketRef.current.on("aiChatCreated", async (data) => {
      console.log("Received aiChatCreated event:", data);
      await refreshChat();
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  let history = [];
  const fetchSuggestions = async (messageHistory) => {
    const userId = localStorage.getItem("id");
    console.log("avatar data in fetch suggestion: ", avatarData);
    if (!avatarData?.greetingLanguage) {
      return;
    }
    try {
      // Only proceed with fetching suggestions if assistant is onboarded
      console.log("avatar Data: ", avatarData);

      const payload = {
        user_id: userId,
        language: avatarData?.greetingLanguage,
        ...(messageHistory?.length > 0 && {
          messages: messageHistory.map((msg) => ({
            role: msg.isUser ? "human" : "ai",
            content: msg.text,
          })),
        }),
      };

      const response = await fetch(`${CHAT_BASE_API_URL}/suggestions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x_auth_token": localStorage.getItem("token"),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.status === 400) {
        showSnackbar(data.text, "error");
        return;
      }
      console.log("Suggestions data:", data);
      const parsedSuggestions = data.suggestions
        ? JSON.parse(data.suggestions.replace(/'/g, '"'))
        : [];
      setSuggestions(parsedSuggestions);
      setShowSuggestions(true);

      // Add a small delay to ensure DOM updates before scrolling
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    if (messages.length > 0 && avatarData?.isAssistantOnboarded) {
      fetchSuggestions(messages);
    }
  }, [avatarData?.greetingLanguage, messages.length]);

  const fetchChatHistory = async () => {
    const userId = localStorage.getItem("id");
    const response = await fetch(
      `${API_BASE_URL}/api/auth/avatar-chat?user_id=${userId}`,
    );
    const data = await response.json();
    console.debug("Chat History Data");
    if (data.responseCode === 200) {
      const formattedMessages = data.avatarChats.flatMap((chat) => {
        return [
          {
            _id: chat._id,
            role: "human",
            content: chat.query,
            files: chat.files?.map((fileUrl) => formatFileFromUrl(fileUrl)),
            userTimestamp: chat.userTimestamp,
            isGreetingMsg: chat.isGreetingMsg,
          },
          {
            _id: chat._id,
            role: "ai",
            content: chat.content,
            avatarTimestamp: chat.avatarTimestamp,
            isGreetingMsg: chat.isGreetingMsg,
            ...(chat.is_dynamic && {
              is_dynamic: chat.is_dynamic,
              workflow_data: chat.workflow_data,
            }),
            fileUrl: chat.url_to_send,
          },
        ];
      });
      history = formattedMessages;
      const formattedDisplayMessages = formattedMessages.map((msg) => ({
        _id: msg._id,
        id:
          msg.role === "human"
            ? msg.content + "-query"
            : msg.content + "-response",
        text: msg.content,
        isUser: msg.role === "human",
        files: msg.role === "human" ? msg.files : [],
        userTimestamp:
          msg.role === "human" ? new Date(parseInt(msg.userTimestamp)) : null,
        avatarTimestamp:
          msg.role === "ai" ? new Date(parseInt(msg.avatarTimestamp)) : null,
        isGreetingMsg: msg.isGreetingMsg,
        ...(msg.role === "ai" &&
          msg.is_dynamic && {
          is_dynamic: msg.is_dynamic,
          workflow_data: msg.workflow_data,
        }),
      }));
      const isLastMessageContainFile =
        isLastMessageContainFileUrl(formattedMessages);
      // ✅ If the last message contains file URLs, attach them to the last message
      if (isLastMessageContainFile) {
        const lastMsgIndex = formattedDisplayMessages.length - 1;
        const lastMessage = formattedMessages[formattedMessages.length - 1];

        if (lastMessage.fileUrl) {
          formattedDisplayMessages[lastMsgIndex].fileurl = lastMessage.fileUrl;
        }
      }

      // Add a "Loading..." message for the greeting
      formattedDisplayMessages.push({
        files: [],
        id: "greeting-loading",
        isUser: false,
        text: t("avatarChat.loading"),
        isGreetingMsg: false,
      });
      return formattedDisplayMessages;
    }
    return [];
  };

  const refreshChat = async () => {
    setIsChatting(true);
    try {
      const userId = localStorage.getItem("id");
      const response = await fetch(
        `${API_BASE_URL}/api/auth/avatar-chat?user_id=${userId}`,
      );
      const data = await response.json();
      console.debug("Refresh data", data);
      if (data.responseCode === 200) {
        const formattedMessages = data.avatarChats.flatMap((chat) => [
          {
            _id: chat._id,
            role: "human",
            content: chat.query,
            files: chat.files.map((fileUrl) => formatFileFromUrl(fileUrl)),
            userTimestamp: chat.userTimestamp,
            isGreetingMsg: chat.isGreetingMsg,
          },
          {
            _id: chat._id,
            role: "ai",
            content: chat.content,
            avatarTimestamp: chat.avatarTimestamp,
            isGreetingMsg: chat.isGreetingMsg,
            fileurl: chat.url_to_send,
          },
        ]);

        // Check last message for workflow data and dynamic status
        const lastMessage = formattedMessages[formattedMessages.length - 1];
        if (
          lastMessage &&
          lastMessage.role === "ai" &&
          lastMessage.content.includes("<WORKFLOW>")
        ) {
          setTimeout(() => {
            refreshChat();
          }, 20000);
        }

        history = formattedMessages;
        const formattedDisplayMessages = formattedMessages.map((msg, index) => {
          // Check if this is the last message, it's from the AI, and has a fileurl
          const isLastMessageWithFile =
            index === formattedMessages.length - 1 &&
            msg.role === "ai" &&
            msg.fileurl;

          return {
            _id: msg._id,
            id:
              msg.role === "human"
                ? msg.content + "-query"
                : msg.content + "-response",
            text: msg.content,
            isUser: msg.role === "human",
            files: msg.role === "human" ? msg.files : [],
            userTimestamp:
              msg.role === "human"
                ? new Date(parseInt(msg.userTimestamp))
                : null,
            avatarTimestamp:
              msg.role === "ai"
                ? new Date(parseInt(msg.avatarTimestamp))
                : null,
            isGreetingMsg: msg.isGreetingMsg,
            ...(isLastMessageWithFile ? { fileurl: msg.fileurl } : {}),
          };
        });
        setMessages(formattedDisplayMessages);
        fetchSuggestions(formattedDisplayMessages);
        setClearingChat(false);
      }
    } catch (error) {
      console.error("Error refreshing chat:", error);
    } finally {
      setClearingChat(false);
      setIsChatting(false);
    }
  };
  // In the useEffect, modify the chat initialization

  const initializeChat = async () => {
    setIsChatting(true);
    try {
      // Avatar Loading
      setIsAvatarLoading(true);
      setIsUpdatingOnboarding(true);
      const avatarResponse = await fetchAvatarData();
      if (avatarResponse.responseCode === 404) {
        navigate("/avatar-selection");
        return;
      }
      if (avatarResponse.responseCode === 200) {
        setAvatarData(avatarResponse.avatar);
        setIsUpdatingOnboarding(false);
        setIsAvatarLoading(false);
      }

      // Chat History Loading
      setIsChatHistoryLoading(true);
      const formattedMessages = await fetchChatHistory();

      let updatedMessages = [...formattedMessages];

      // Remove the greeting loading message first
      updatedMessages = updatedMessages.filter(
        (msg) => msg.id !== "greeting-loading",
      );
      // Check if we should restore the pending message

      // Continue with normal initialization
      setMessages(formattedMessages);
      setIsChatHistoryLoading(false);

      // Fetch greeting while showing "Loading..." message
      const userId = localStorage.getItem("id");
      const userName = localStorage.getItem("name");

      handleGreetingInitialization(
        formattedMessages,
        userId,
        userName,
        avatarResponse.avatar,
      );
    } catch (error) {
      console.error("Error initializing chat:", error);
      setIsUpdatingOnboarding(false);
      setIsAvatarLoading(false);
      setIsChatHistoryLoading(false);
    } finally {
      setInitialLoadComplete(true);
      setIsChatting(false);
    }
  };

  const handleGreetingInitialization = async (
    formattedMessages,
    userId,
    userName,
    avatarData,
  ) => {
    try {
      // Get the last greeting message
      const greetingMessages = formattedMessages.filter(
        (msg) => msg?.isGreetingMsg === true,
      );
      const lastMessage = greetingMessages[greetingMessages.length - 1];
      const lastmsg = formattedMessages[formattedMessages.length - 2];
      if (lastmsg?.is_dynamic) {
        setIsDynamic(lastmsg.is_dynamic);
        setWorkflowData(lastmsg.workflow_data);
      }

      const currentTime = Date.now();
      const fifteenMinutes = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds just to remove welcome message after certain period (previously we showed welcome msg if last welcome msg is 15 minutes old, now we don't need this functionality. So, it simply set to 30 days, so that the main functionality is not affected)

      // Check if we need a new greeting (no last message or message is older than 15 mins)
      const needsNewGreeting =
        !lastMessage ||
        !lastMessage.avatarTimestamp ||
        currentTime - new Date(lastMessage.avatarTimestamp).getTime() >
        fifteenMinutes;

      if (needsNewGreeting) {
        try {
          // Make API call for new greeting
          const response = await fetch(`${CHAT_BASE_API_URL}/chat`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-auth-token": localStorage.getItem("token") || ""
            },
            body: JSON.stringify({
              user_id: userId,
              user_name: userName,
              messages: history,
              timestamp: formatTimestamp(new Date()),
              timestampinunix: Date.now(),
              s3_document_urls: [],
              is_greeting: true,
              language: avatarData.greetingLanguage,
              ...(avatarData.isAssistantOnboarded === false && {
                is_onboarded: false,
              }),
            }),
          });
          if (response.status === 400) {
            showSnackbar(response.text, "error");
            return;
          }
          if (!response.ok) {
            throw new Error(`API call failed with status: ${response.status}`);
          }

          const newData = await response.json();

          if (newData?.content && newData?.content.includes("<Tool>")) {
            return;
          }

          const avatarTimestamp = Date.now();

          // Save the new greeting chat
          await saveChat(
            userId,
            "",
            newData.content,
            [],
            null,
            avatarTimestamp,
            true,
          );

          // Update messages state
          setMessages((prevMessages) => {
            const updatedMessages = prevMessages.map((msg) =>
              msg.id === "greeting-loading"
                ? {
                  files: [],
                  id: `${userId}-greeting`,
                  isUser: false,
                  text: newData.content,
                  userTimestamp: null,
                  avatarTimestamp: avatarTimestamp,
                  isGreetingMsg: true,
                }
                : msg,
            );

            // Fetch suggestions with updated messages
            fetchSuggestions(updatedMessages);

            return updatedMessages;
          });
        } catch (error) {
          console.error("Error handling greeting:", error);
          // Remove loading message in case of error
          setMessages((prevMessages) =>
            prevMessages.filter((msg) => msg.id !== "greeting-loading"),
          );
          fetchSuggestions(
            formattedMessages.filter((msg) => msg.id !== "greeting-loading"),
          );
        }
      } else {
        // No new greeting needed, just remove loading message and fetch suggestions
        setMessages((prevMessages) =>
          prevMessages.filter((msg) => msg.id !== "greeting-loading"),
        );
        fetchSuggestions(
          formattedMessages.filter((msg) => msg.id !== "greeting-loading"),
        );
      }
    } catch (error) {
      console.error("Error in greeting initialization:", error);
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== "greeting-loading"),
      );
    }
  };

  useEffect(() => {
    if (activeSection === "chat") {
      initializeChat();
    }
  }, [navigate]);

  // Separate useEffect for handling scrolling
  useEffect(() => {
    if (bottomRef.current && (messages.length > 0 || initialLoadComplete)) {
      bottomRef.current.scrollIntoView({
        behavior: initialLoadComplete ? "smooth" : "auto",
      });
    }
  }, [messages, initialLoadComplete]);

  // Additional useEffect to handle suggestion changes
  useEffect(() => {
    if (showSuggestions && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showSuggestions]);

  const handleClearChat = async () => {
    const userId = localStorage.getItem("id");
    const userName = localStorage.getItem("name");

    try {
      setClearingChat(true);
      const response = await fetch(
        `${API_BASE_URL}/api/auth/delete-avatar-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": localStorage.getItem("token"),
          },
          body: JSON.stringify({
            user_id: userId,
          }),
        },
      );

      const data = await response.json();
      if (data.responseCode === 200) {
        const responseNew = await fetch(`${CHAT_BASE_API_URL}/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": localStorage.getItem("token") || ""
          },
          body: JSON.stringify({
            user_id: userId,
            user_name: userName,
            messages: [],
            timestamp: formatTimestamp(new Date()),
            timestampinunix: Date.now(),
            s3_document_urls: [],
            is_greeting: true,
            language: avatarData.greetingLanguage,
          }),
        });
        if (responseNew.status === 400) {
          showSnackbar(responseNew.text, "error");
          return;
        }
        const newData = await responseNew.json();
        if (newData?.content && newData?.content.includes("<Tool>")) {
          return;
        }
        const avatarTimestamp = Date.now();
        // Save Greeting Chat
        await saveChat(
          userId,
          "",
          newData.content,
          [],
          null,
          avatarTimestamp,
          true,
        );
        setShowSuggestions(false);
        setMessages([
          {
            files: [],
            id: "6748965e0d35f8e8816fd171-greeting",
            isUser: false,
            text: newData.content,
            userTimestamp: null,
            avatarTimestamp: avatarTimestamp,
            isGreetingMsg: true,
          },
        ]);
      } else {
        console.error("Failed to clear chat:", data.message);
      }
    } catch (error) {
      console.error("Error clearing chat:", error);
    } finally {
      setClearingChat(false);
    }
  };
  const handleClearDbChat = async () => {
    const userId = localStorage.getItem("id");
    try {
      setClearingChat(true);
      const response = await fetch(
        `${API_BASE_URL}/api/auth/delete-chats`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId,
          }),
        },
      );

      const data = await response.json();
      if (data.responseCode === 200) {
        toast.success(data.message);
        setDbMessage([]);
      } else {
        console.error("Failed to delete chats:", data.message);
      }
    } catch (error) {
      toast.error("An error occurred while clearing the chat.");
    } finally {
      setClearingChat(false);
    }
  };

  const handleClearSkillChat = async () => {
    const userId = localStorage.getItem("id");
    console.log(userId);

    // Clear the UI immediately to prevent race conditions
    setSkillChat([]);

    try {
      setClearingChat(true);
      const response = await fetch(
        `${API_BASE_URL}/api/auth/delete-skills-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId,
          }),
        },
      );

      const data = await response.json();
      if (data.responseCode === 200) {
        toast.success(data.message);
      } else {
        console.error("Failed to delete chats:", data.message);
      }
    } catch (error) {
      toast.error("An error occurred while clearing the chat.");
    } finally {
      setClearingChat(false);
    }
  };
  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const handleCloseTopPopover = () => {
    setTopPopoverAnchorEl(null);
  };
  const topPopoverOpen = Boolean(topPopoverAnchorEl);
  const topPopoverId = topPopoverOpen ? "top-popover" : undefined;

  // Chat Input Popover handlers
  const handleOpenChatPopover = (event) => {
    setChatPopoverAnchorEl(event.currentTarget);
  };
  const handleCloseChatPopover = () => {
    setChatPopoverAnchorEl(null);
  };
  const chatPopoverOpen = Boolean(chatPopoverAnchorEl);
  const chatPopoverId = chatPopoverOpen ? "chat-popover" : undefined;

  const handleDriveFiles = async (fileLinks) => {
    setIsDriveFilesLoading(true);
    handleCloseChatPopover();

    if (!fileLinks || fileLinks.length === 0) {
      setIsDriveFilesLoading(false);
      return;
    }

    const fileId = fileLinks[0]; // Take only the first file
    const apiKey = process.env.REACT_APP_API_KEY;
    const accessToken = localStorage.getItem("access_token");
    let response = null;
    try {
      let fileUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&key=${apiKey}`;

      // Fetch the file content (blob)
      response = await fetch(fileUrl);

      if (!response.ok) {
        response = await fetch(fileUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        if (!response.ok) {
          const errorData = await response.json();
          console.error("Metadata fetch error:", errorData);
          if (response.status === 404) {
            throw new Error(
              "File not found. You may not have permission to access this file.",
            );
          }
          throw new Error(`Metadata error: ${response.statusText}`);
        }
      }

      const blobData = await response.blob();

      // Fetch metadata (name, type)
      const metadataUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?key=${apiKey}&fields=name,mimeType`;
      let metadataResponse = await fetch(metadataUrl);

      if (!metadataResponse.ok) {
        metadataResponse = await fetch(metadataUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
        if (!metadataResponse.ok) {
          const errorData = await metadataResponse.json();
          console.error("Metadata fetch error:", errorData);
          throw new Error(
            `Error fetching metadata: ${metadataResponse.statusText}`,
          );
        }
      }

      const metadata = await metadataResponse.json();

      // Create a File object
      const file = new File([blobData], metadata.name, {
        type: metadata.mimeType,
      });

      // Upload to S3
      const s3Url = await uploadFileToS3(file);
      console.log("S3 URL:", s3Url);

      if (!s3Url) throw new Error("S3 upload failed");

      const newFile = {
        name: metadata.name,
        type: metadata.mimeType,
        isDrive: true,
        driveUrl: s3Url,
        id: fileId,
      };

      // Add to uploaded files
      setUploadedFiles((prevFiles) => [...prevFiles, newFile]);
    } catch (error) {
      toast.error(error.message);
      console.error("Error:", error.message);
    } finally {
      setIsDriveFilesLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    handleCloseChatPopover();
    const files = Array.from(e.target.files).map((file) => ({
      name: file.name,
      type: file.type,
      file: file,
      isDrive: false,
    }));
    setUploadedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const removeFile = (fileName) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileName),
    );
  };

  const sendMessage = async (
    messageContent,
    files,
    isDynamicInput = false,
    dynamicWorkflowData = null,
  ) => {
    const userId = localStorage.getItem("id");
    const userName = localStorage.getItem("name");
    const userMessage = {
      role: "human",
      content: messageContent,
      files: files,
    };

    setShowSuggestions(false);
    const userTimestamp = Date.now();
    console.log("Check message stateb", messages);

    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: prevMessages.length + 1,
        text: messageContent,
        isUser: true,
        files: files,
        userTimestamp: userTimestamp,
        avatarTimestamp: null,
      },
    ]);

    // ✅ Now add loading message
    setMessages((prevMessages) => [
      ...prevMessages,
      { id: prevMessages.length + 1, text: "Loading...", isUser: false },
    ]);
    // return

    console.log("Check message state", messages);

    let failedUrls = [];

    const fileUrls = (
      await Promise.all(
        files.map(async (file) => {
          if (file.isDrive) {
            return file.driveUrl;
          } else {
            const fileUrl = await uploadFileToS3(file.file);
            if (!fileUrl) {
              failedUrls.push(file.file.name);
              return null;
            }
            return fileUrl;
          }
        }),
      )
    ).filter(Boolean); // Remove nulls

    if (failedUrls.length > 0 && fileUrls.length === 0) {
      toast.error(
        t("avatarChat.failedToUpload", { files: failedUrls.join(", ") }),
      );
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.text === "Loading..."
            ? {
              ...msg,
              text: t("avatarChat.errorSendingMessage"),
              isUser: false,
            }
            : msg,
        ),
      );
      return;
    }

    let isOnboarded = avatarData?.isAssistantOnboarded;
    if (!isOnboarded) {
      isOnboarded = await checkAssistantOnboarded(userId);
      avatarData.isAssistantOnboarded = isOnboarded;
    }

    try {
      setIsChatting(true);
      const response = await fetch(`${CHAT_BASE_API_URL}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": localStorage.getItem("token") || ""
        },

        body: JSON.stringify({
          user_id: userId,
          user_name: userName,
          messages: [
            ...messages.map((msg) => ({
              role: msg.isUser ? "human" : "ai",
              content: msg.text,
            })),
            userMessage,
          ],
          timestamp: formatTimestamp(new Date()),
          timestampinunix: Date.now(),
          s3_document_urls: fileUrls,
          ...(isOnboarded === false && {
            is_onboarded: false,
          }),
          language: avatarData.greetingLanguage,
          ...(isDynamicInput && {
            is_dynamic: isDynamicInput,
            workflow_data: dynamicWorkflowData,
          }),
        }),
      });
      if (response.status === 400) {
        showSnackbar(response.text, "error");
        return;
      }
      console.log("Chat Response Status:", response);
      const data = await response.json();
      if (data?.content && data?.content.includes("<Tool>")) {
        return;
      }
      const avatarTimestamp = Date.now();

      // Handle dynamic workflow response
      if (data.is_dynamic && data.workflow_data) {
        setIsDynamic(true);
        setWorkflowData(data.workflow_data);
      }

      setMessages((prevMessages) => {
        const updatedMessages = prevMessages.map((msg) =>
          msg.text === "Loading..."
            ? {
              ...msg,
              text: data.content,
              isUser: false,
              ...(data.url ? { fileurl: data.url } : {}),
              userTimestamp: null,
              avatarTimestamp: avatarTimestamp,
            }
            : msg,
        );

        const lastMessage = updatedMessages[updatedMessages.length - 1];
        if (!lastMessage?.isUser && lastMessage?.text?.includes("<WORKFLOW>")) {
          setTimeout(async () => {
            await refreshChat();
          }, 20000);
        }
        fetchSuggestions(updatedMessages);
        return updatedMessages;
      });

      await saveChat(
        userId,
        messageContent,
        data.content,
        fileUrls,
        userTimestamp,
        avatarTimestamp,
        false,
        data.is_dynamic,
        data.workflow_data,
      );
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.text === "Loading..."
            ? { ...msg, text: "Error sending message", isUser: false }
            : msg,
        ),
      );
      setShowSuggestions(true);
    } finally {
      setIsChatting(false);
    }
  };
  const sendSkillChatMessage = async (messageContent, files) => {
    const userId = localStorage.getItem("id");
    const timestampNow = Date.now();
    let failedUrls = [];
    setIsChatting(true);

    try {
      // ✅ Upload files first
      const fileUrls = (
        await Promise.all(
          files.map(async (file) => {
            const fileUrl = await uploadFileToS3(file.file);
            if (!fileUrl) {
              failedUrls.push(file.file.name);
              return null;
            }
            return fileUrl;
          }),
        )
      ).filter(Boolean);

      // ✅ Build user message object
      const userMessage = {
        role: "human",
        content: messageContent,
        s3_document_urls: fileUrls,
      };

      // ✅ Add user message instantly in chat
      setSkillChat((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          text: messageContent,
          isUser: true,
          files,
          userTimestamp: timestampNow,
          avatarTimestamp: null,
        },
        {
          id: prev.length + 2,
          text: "Loading...",
          isUser: false,
        },
      ]);

      console.debug("Before message send, skillChat:", skillChat);
      if (failedUrls.length > 0 && fileUrls.length === 0) {
        toast.error(`Failed to upload files: ${failedUrls.join(", ")}`);
        setMessages((prevMessages) =>
          prevMessages.map((msg) =>
            msg.text === "Loading..."
              ? { ...msg, text: "Error sending message", isUser: false }
              : msg,
          ),
        );
        return;
      }

      // ✅ Prepare API payload
      const payload = {
        user_id: userId,
        messages: [
          ...skillChat.map((msg) => ({
            role: msg.isUser ? "human" : "ai",
            content: msg.text,
          })),
          userMessage,
        ],
        timestamp: formatTimestamp(new Date()),
        s3_document_urls: fileUrls,
        language: avatarData.greetingLanguage,
      };

      // ✅ Send to API
      const response = await fetch(`${CHAT_BASE_API_URL}/query_skills`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-auth-token": localStorage.getItem("token") || "" },
        body: JSON.stringify(payload),
      });
      if (response.status === 400) {
        showSnackbar(response.text, "error");
        return;
      }
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const data = await response.json();
      console.debug("Skill chat response data:", data);
      const avatarTimestamp = Date.now();

      // ✅ Replace "Loading..." with AI response
      setSkillChat((prev) =>
        prev.map((msg) =>
          msg.text === "Loading..."
            ? {
              ...msg,
              text: data.content,
              isUser: false,
              ...(data.url && { fileurl: data.url }),
              avatarTimestamp,
              userTimestamp: null,
            }
            : msg,
        ),
      );

      await handleSaveChat("chat-with-skills", {
        user_id: userId,
        query: messageContent,
        content: data.content || data.error,
        files: fileUrls || [],
        userTimestamp: timestampNow,
        avatarTimestamp: avatarTimestamp,
      });
    } catch (error) {
      console.error("Error sending message:", error);

      // ✅ Replace "Loading..." with error message
      setSkillChat((prev) =>
        prev.map((msg) =>
          msg.text === "Loading..."
            ? { ...msg, text: "⚠️ Error sending message", isUser: false }
            : msg,
        ),
      );
    } finally {
      setIsChatting(false);
    }
  };

  const sendDatabaseMessage = async (userMessage) => {
    const userName = localStorage.getItem("name");
    const userId = localStorage.getItem("id");
    if (!userMessage.trim()) return;

    const newMessages = [
      ...dbmessage,
      { role: "USER", content: userMessage, sourceDocuments: [] },
    ];
    setDbMessage(newMessages);

    try {
      setIsChatting(true);
      const response = await fetch(
        `${API_BASE_URL}/api/auth/get-training-status?user_id=${userId}`,
      );
      const data = await response.json();

      if (data.isTrained) {
        const queryResponse = await fetch(
          "https://flowproai.aivoria.nl/ai_flow_pro/query_multidoc",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              user_id: userId,
              user_name: userName,
              // source_document_id: selectedDocument,
              messages: newMessages,
              language: avatarData.greetingLanguage,
            }),
          },
        );
        setIsChatting(false);
        const queryData = await queryResponse.json();
        const updatedMessages = [
          ...newMessages,
          {
            role: "ASSISTANT",
            content: queryData.content,
            sourceDocuments: [],
            sourceChunks: [],
          },
        ];
        setDbMessage(updatedMessages);
        // Only call the API if SourceDiv is not visible
        await fetch(`${API_BASE_URL}/api/auth/create-chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": localStorage.getItem("token"),
          },
          body: JSON.stringify({
            user_id: userId,
            user_name: userName,
            query: userMessage,
            content: queryData.content,
            timestamp: formatTimestamp(new Date()),
            timestampinunix: Date.now(),
            sourceDocuments: [],
            sourceChunks: [],
          }),
        });
      } else {
        const message =
          data.trainingStatus ===
            "Embeddings are being created in the background."
            ? "Your model is training. Wait a few seconds and then send a message."
            : "No training files! Upload some files first";
        setDbMessage([...newMessages, { role: "ASSISTANT", content: message }]);
      }
    } catch (error) {
      setDbMessage([
        ...newMessages,
        { role: "ASSISTANT", content: "An error occurred. Please try again." },
      ]);
    } finally {
      setIsChatting(false);
    }
  };

  const handleSendMessage = async (
    inputMessage = "",
    skillFiles = [],
    sectionOverride,
  ) => {
    console.log("inputMessage", inputMessage);
    console.log("skillFiles", skillFiles);
    const section = sectionOverride ?? activeSection;
    const message =
      (typeof inputMessage === "string" ? inputMessage.trim() : "") ||
      (typeof inputValue === "string" ? inputValue.trim() : "");

    if (message === "") return;
    setInputValue("");
    console.log("Active Section:", activeSection);
    console.log("Section:", section);
    console.log(section === "chat-with-skills");
    if (section === "chat-with-skills" || activeSection == "chat-with-skills") {
      console.log("Sending skill chat message");
      const currentUploadFile = [...uploadedFiles, ...skillFiles];
      setUploadedFiles([]);
      await sendSkillChatMessage(message, currentUploadFile);
    } else if (activeSection == "chat-with-db") {
      await sendDatabaseMessage(message);
    } else {
      const currentUploadedFiles = [...uploadedFiles, ...skillFiles];
      setUploadedFiles([]);

      // Check for dynamic workflow
      if (isDynamic && Object.keys(workflowData).length > 0) {
        const currentWorkflowData = { ...workflowData };
        setIsDynamic(false);
        setWorkflowData({});
        await sendMessage(
          message,
          currentUploadedFiles,
          true,
          currentWorkflowData,
        );
      } else {
        await sendMessage(message, currentUploadedFiles);
      }
    }
  };

  const avatarImage = avatarData
    ? AVATARS.find((avatar) => avatar.imageId === avatarData.imageId)?.src
    : null;

  const [isDragging, setIsDragging] = useState(false);
  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    const files = Array.from(droppedFiles).map((file) => ({
      name: file.name,
      type: file.type,
      file: file,
      isDrive: false,
    }));
    setUploadedFiles((prevFiles) => [...prevFiles, ...files]);
    setIsDragging(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    const isOutside = !event.currentTarget.contains(event.relatedTarget);
    if (isOutside) {
      setIsDragging(false);
    }
  };

  const renderAvatarSection = () => {
    const avatarImage = avatarData
      ? AVATARS.find((avatar) => avatar.imageId === avatarData.imageId)?.src
      : null;

    return (
      <AvatarSection onClick={() => navigate("/avatar-selection")}>
        <img
          src={avatarImage}
          alt="Avatar"
          style={{
            width: "2.5rem",
            height: "2.5rem",
            borderRadius: "60%",
            background: "#FFF",
          }}
        />
        <AssistantName>
          {avatarData ? avatarData.avatarName : "Michael"}
        </AssistantName>
        {/* <EditImg
          src={EditIcon}
          alt="edit"
          onClick={() => navigate("/avatar-selection")}
        /> */}
        {/* <AssistantTitle>Your Personal Assistant</AssistantTitle> */}
      </AvatarSection>
    );
  };

  const renderChatContent = () => {
    if (isChatHistoryLoading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </div>
      );
    }
    return (
      <ChatDiv>
        {messages.map((message, index) => (
          <ChatMessage
            _id={message._id}
            key={index}
            text={message.text}
            isUser={message.isUser}
            avatarImage={
              avatarData
                ? AVATARS.find(
                  (avatar) => avatar.imageId === avatarData.imageId,
                )?.src
                : null
            }
            avatarData={avatarData.avatarName}
            files={message.files || []}
            fileUrl={message?.fileurl}
            userColor="#202226"
            userTextColor="#FFFFFF"
            avatarColor="#FFF"
            avatarTextColor="#202226"
            userTimestamp={message.userTimestamp}
            avatarTimestamp={message.avatarTimestamp}
          />
        ))}
        <div ref={bottomRef} />
      </ChatDiv>
    );
  };

  const renderNotificationSection = () => {
    return (
      <NotificationSection onClick={() => navigate("/notifications")}>
        <img
          width="24px"
          height="24px"
          src={NotificationWhiteIcon}
          alt="notifications"
        />
      </NotificationSection>
    );
  };
  const handleDeleteChat = async () => {
    try {
      if (activeSection === "chat-with-db") {
        await handleClearDbChat();
        return;
      } else if (activeSection === "chat") {
        await handleClearChat();
      } else if (activeSection === "chat-with-skills") {
        await handleClearSkillChat();
      }
      console.log("Clear chat clicked");
    } catch (error) {
      console.error("Error clearing chat:", error.message);
    } finally {
      handleCloseTopPopover();
    }
  };
  const renderTopNavbarSection = () => {
    const updateAssistantOnboarded = async (userId) => {
      setIsUpdatingOnboarding(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/auth/update-assistant-onboarded`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId }),
          },
        );
        const data = await response.json();
        setIsUpdatingOnboarding(false);
        if (data.responseCode === 200) {
          return true;
        } else {
          return false;
        }
      } catch (error) {
        setIsUpdatingOnboarding(false);
        return false;
      }
    };

    let nav = [
      {
        title: t("avatarChat.chatWith", { name: avatarData?.avatarName || "" }),
        isActive: activeSection === "chat",
        onClick: () => {
          setActiveSection("chat");
        },
        content: avatarData?.avatarName ? (
          t("avatarChat.chatWith", { name: avatarData?.avatarName })
        ) : (
          <CircularProgress size={20} />
        ),
      },
      {
        title: t("avatarChat.chatWithStorage"),
        isActive: activeSection === "chat-with-db",
        onClick: () => {
          setActiveSection("chat-with-db");
          setUploadedFiles([]);
        },
        content: avatarData?.avatarName ? (
          t("avatarChat.chatWithStorage")
        ) : (
          <CircularProgress size={20} />
        ),
      },
      {
        title: t("avatarChat.chatWithSkills"),
        isActive: activeSection === "chat-with-skills",
        onClick: () => {
          setActiveSection("chat-with-skills");
        },
        content: avatarData?.avatarName ? (
          t("avatarChat.chatWithSkills")
        ) : (
          <CircularProgress size={20} />
        ),
      },
      {
        title: t("avatarChat.skills"),
        isActive: activeSection === "skills",
        onClick: () => {
          setActiveSection("skills");
        },
        content: avatarData?.avatarName ? (
          t("avatarChat.skills")
        ) : (
          <CircularProgress size={20} />
        ),
      },
    ];

    if (avatarData?.isAssistantOnboarded === false) {
      nav.push({
        title: t("avatarChat.skipOnboarding"),
        isActive: false,
        onClick: async () => {
          const userId = localStorage.getItem("id");
          const isOnboarded = await updateAssistantOnboarded(userId);
          if (isOnboarded) {
            avatarData.isAssistantOnboarded = true;
          }
        },
        content: isUpdatingOnboarding ? (
          <CircularProgress size={20} />
        ) : (
          t("avatarChat.skipOnboarding")
        ),
      });
    }
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "calc(100% - 135px)",
        }}
      >
        <TopNavbarSection>
          <div style={{ display: "flex", alignItems: "center", gap: "40px" }}>
            {nav.slice(0, -1).map((item, index) => (
              <TopNavItem
                key={index}
                isActive={item.isActive}
                onClick={() => {
                  if (!clearingChat || item.title !== "Clear Chat") {
                    item.onClick();
                  }
                }}
                style={item.style}
              >
                {item.content || item.title}
              </TopNavItem>
            ))}
          </div>
        </TopNavbarSection>

        <TopNavbarSection>
          {nav.length > 0 && (
            <TopNavItem
              isActive={nav[nav.length - 1].isActive}
              onClick={() => {
                if (
                  !clearingChat ||
                  nav[nav.length - 1].title !== "Clear Chat"
                ) {
                  nav[nav.length - 1].onClick();
                }
              }}
              style={nav[nav.length - 1].style}
            >
              {nav[nav.length - 1].content || nav[nav.length - 1].title}
            </TopNavItem>
          )}
        </TopNavbarSection>
      </div>
    );
  };

  const renderRightNavbarSection = () => {
    const sideNav = [
      {
        title: t("avatarChat.aiAssistant"),
        icon: AvatarChatHomeIcon,
        onClick: (event) => {
          event.preventDefault();
        },
        id: "ai_assistant",
        isActive: true,
      },
      {
        title: t("avatarChat.clearChat", { section: activeSection }),
        icon: ClearChat,
        onClick: (event) => {
          event.preventDefault();
          handleDeleteChat();
        },
        id: "clear_chat",
        isActive: false,
      },
      {
        title: t("avatarChat.addDocument"),
        icon: PlusIconWhite,
        onClick: (event) => {
          event.preventDefault();
          handleOpenChatPopover(event);
        },
        id: "add_document",
        isActive: false,
      },
    ];

    const [showBelow, setShowBelow] = useState(false);
    const handleShowBelow = () => {
      setShowBelow(!showBelow);
    };
    const belowNav = [
      {
        title: "Transcriber",
        icon: AvatarChatCameraIcon,
        onClick: (event) => {
          event.preventDefault();
          navigate("/transcriber");
        },
        isActive: false,
      },
    ];
    return (
      <RightNavbarSection>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {sideNav
            .filter(
              (item) =>
                !(
                  activeSection === "chat-with-db" && item.id === "add_document"
                ),
            )
            .map((item, index) => (
              <Tooltip title={item.title} key={index}>
                <RightNavItem
                  onClick={(event) => {
                    item.onClick(event);
                    setActiveIcon(item.id);
                  }}
                  style={{
                    background:
                      item.id === activeIcon ? "rgba(17, 17, 17, 0.2)" : "",
                  }}
                >
                  <img
                    style={{
                      width: "20px",
                      height: "20px",
                      transform:
                        item.title === "More" && showBelow
                          ? "rotate(180deg)"
                          : "none",
                      transition: "transform 0.5s ease",
                    }}
                    src={item.icon}
                    alt={item.title}
                  />
                </RightNavItem>
              </Tooltip>
            ))}

          {showBelow && (
            <BelowNavbarSection>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                {belowNav.map((item, index) => (
                  <Tooltip title={item.title}>
                    <RightNavItem
                      key={index}
                      onClick={(event) => {
                        event.preventDefault();
                        item.onClick(event);
                      }}
                      style={{ width: "36px", height: "36px" }}
                    >
                      <img
                        style={{ width: "20px", height: "20px" }}
                        src={item.icon}
                        alt={item.title}
                      />
                    </RightNavItem>
                  </Tooltip>
                ))}
              </div>
            </BelowNavbarSection>
          )}
        </div>
      </RightNavbarSection>
    );
  };

  const fetchTokens = async (authCode) => {
    try {
      const response = await fetch("https://oauth2.googleapis.com/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          code: authCode,
          client_id: process.env.REACT_APP_CLIENT_ID,
          client_secret: process.env.REACT_APP_CLIENT_SECRET,
          redirect_uri: process.env.REACT_APP_AVATAR_REDIRECT_URI,
          grant_type: "authorization_code",
        }),
      });

      const data = await response.json();
      if (data.error) {
        console.error("Error fetching tokens:", data.error);
        return; // Handle errors gracefully
      }

      const { access_token, refresh_token, expires_in } = data;

      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("expires_in", expires_in);
    } catch (error) {
      console.error("Error fetching tokens:", error);
    }
  };
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get("code");

    if (authCode) {
      fetchTokens(authCode);
    }
  }, []);

  return (
    <Container
      isOpen={isSidebarOpen}
      style={{
        marginRight: "0rem",
        backgroundColor: "#F3F1F8",
        height: "100vh",
        // padding: "1.5rem",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
          gap: "1rem",
          padding: "1.5rem 1.5rem 0 1.5rem",
        }}
      >
        {renderNotificationSection()}
        {renderAvatarSection()}
      </div>
      <InnerContainer>
        {renderTopNavbarSection()}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "2.5rem",
            marginTop: "1rem",
          }}
        >
          <Content
            style={{
              height:
                uploadedFiles.length > 0
                  ? "calc(100vh - 322px)"
                  : "calc(100vh - 252px)",
              // overflowY: "auto"
            }}
          >
            {activeSection === "chat" ? (
              <ChatBox
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                {isDragging && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    {t("avatarChat.dropFilesHere")}
                  </div>
                )}
                {renderChatContent()}
                {/* {showSuggestions && (
                  <SuggestionMessages
                    suggestions={suggestions}
                    onSuggestionClick={handleSuggestionClick}
                    isPopup={false}
                  />
                )} */}
              </ChatBox>
            ) : activeSection === "chat-with-db" ? (
              <DatabaseChat
                messages={dbmessage}
                setMessages={setDbMessage}
                isChatting={isChatting}
                avatarData={avatarData}
              />
            ) : // :
              //   activeSection === 'chat-with-meetings' ? (
              //     <MeetingChat messages={meetingChat} setMessages={setMeetingChat} />
              //   )
              activeSection === "chat-with-skills" ? (
                <SkillChat
                  messages={skillChat}
                  setMessages={setSkillChat}
                  uploadedFiles={uploadedFiles}
                  avatarData={avatarData}
                  isChatting={isChatting}
                />
              ) : (
                <SkillsSection
                  onRunSkill={(skillValue, skillFiles = [], tool) => {
                    const nextSection =
                      tool?.isSkill === false ? "chat" : "chat-with-skills";

                    setActiveSection(nextSection);
                    handleSendMessage(skillValue, skillFiles, nextSection);
                  }}
                />
              )}
          </Content>
          {renderRightNavbarSection()}
        </div>
        {activeSection !== "skills" && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "2.5rem",
              marginTop: "1rem",
            }}
          >
            <InputContainer
              style={{
                // borderRadius: uploadedFiles.length > 0 ? "1.25rem" : "6.25rem",
                width: "calc(100% - 135px)",
              }}
            >
              {isDriveFilesLoading ? (
                <CircularProgress size={20} style={{ marginLeft: "4rem" }} />
              ) : (
                <FileDiv
                  style={{
                    display: uploadedFiles.length > 0 ? "flex" : "none",
                  }}
                >
                  {uploadedFiles.map((file, index) => (
                    <FileCard
                      key={index}
                      fileName={file?.name}
                      fileType={
                        file?.type?.includes("pdf")
                          ? "pdf"
                          : file?.type?.includes("word")
                            ? "DOCX"
                            : "TXT"
                      }
                      fileIcon={
                        file?.type?.includes("pdf")
                          ? PdfIcon
                          : file?.type?.includes("word")
                            ? DocxIcon
                            : TxtIcon
                      }
                      onRemove={() => removeFile(file?.name)}
                      isPopup={false}
                    />
                  ))}
                </FileDiv>
              )}
              <div style={{ display: "flex", width: "100%", gap: "10px" }}>
                <input
                  type="file"
                  multiple
                  disabled={isChatting}
                  accept=".pdf,.doc,.docx,.txt"
                  style={{ display: "none" }}
                  id="fileInput"
                  onChange={handleFileUpload}
                />
                {activeSection === "chat" ||
                  activeSection === "chat-with-skills" ? (
                  <img
                    src={PaperClip}
                    alt="attach file"
                    onClick={handleOpenChatPopover}
                    style={{ cursor: "pointer" }}
                  />
                ) : null}

                <Popover
                  id={chatPopoverId}
                  open={chatPopoverOpen}
                  anchorEl={chatPopoverAnchorEl}
                  onClose={handleCloseChatPopover}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      background: "#f1f1f5",
                    }}
                  >
                    <label htmlFor="fileInput" style={{ cursor: "pointer" }}>
                      <UploadDiv>
                        <img src={ComputerIcon} alt="computer" />{" "}
                        {t("avatarChat.uploadFromComputer")}
                      </UploadDiv>
                    </label>
                    {activeSection === "chat" && (
                      <AvatarDriveFilePicker
                        onFilesSelected={handleDriveFiles}
                      />
                    )}
                  </div>
                </Popover>
                <Input
                  type="text"
                  placeholder={t("avatarChat.typeMessage")}
                  value={inputValue}
                  disabled={isChatting}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleSendMessage();
                    }
                  }}
                />
              </div>
            </InputContainer>
            <div style={{ width: "60px" }}>
              <SendMessageIcon
                onClick={!isChatting ? handleSendMessage : undefined}
                style={{
                  pointerEvents: isChatting ? "none" : "auto",
                  opacity: isChatting ? 0.5 : 1, // optional: visual feedback
                  cursor: isChatting ? "not-allowed" : "pointer",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M23.1829 10.6924L5.59354 2.11215C5.44226 2.03835 5.27615 2 5.10784 2C4.496 2 4 2.496 4 3.10784V3.13981C4 3.28846 4.01823 3.43656 4.05428 3.58078L5.74153 10.3297C5.78761 10.5141 5.94347 10.6503 6.13231 10.6713L13.5483 11.4953C13.8055 11.5238 14 11.7412 14 12C14 12.2588 13.8055 12.4762 13.5483 12.5047L6.13231 13.3287C5.94347 13.3497 5.78761 13.4859 5.74153 13.6703L4.05428 20.4192C4.01823 20.5635 4 20.7115 4 20.8602V20.8922C4 21.504 4.496 22 5.10784 22C5.27615 22 5.44226 21.9616 5.59354 21.8878L23.1829 13.3076C23.6828 13.0638 24 12.5563 24 12C24 11.4437 23.6828 10.9362 23.1829 10.6924Z"
                    fill="white"
                  />
                </svg>
              </SendMessageIcon>
            </div>
          </div>
        )}
      </InnerContainer>
    </Container>
  );
};

export default AvatarChat;
